import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, ArchiveRestore, Clock, RotateCcw, ShieldCheck, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Progress } from "./ui/progress";
import { Skeleton } from "./ui/skeleton";
import { api, errMsg, fmtDateTime } from "../lib/api";
import { pokeLiveCollections } from "../lib/liveCollections";

const CONFIRM_WORD = "UNDO";

const Stat = ({ value, label, tone = "", testId }) => (
  <div className="rounded-lg border p-2 text-center">
    <p className={`text-lg font-semibold tabular-nums ${tone}`} data-testid={testId}>{value ?? 0}</p>
    <p className="text-[11px] text-muted-foreground leading-tight">{label}</p>
  </div>
);

export const fmtLeft = (seconds) => {
  const s = Math.max(0, parseInt(seconds, 10) || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h >= 1) return `${h}h ${m}m`;
  if (m >= 1) return `${m}m`;
  return "under a minute";
};

/**
 * Phase 145 — Undo one Demand Upload batch (Bulk Excel Upload / Bulk Entry / Bulk Update).
 * Loads the server preview (what will move to the recycle bin, which fields revert, what is protected),
 * requires the admin to type UNDO, then starts the background rollback and polls its progress.
 */
export function UndoBatchDialog({ code, batch, open, onOpenChange, onDone }) {
  const btype = batch?.type;
  const batchId = batch?.batch_id;
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [word, setWord] = useState("");
  const [undo, setUndo] = useState(null);
  const [busy, setBusy] = useState(false);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    if (!open || !batchId) return;
    let cancelled = false;
    setPreview(null); setPreviewError(null); setUndo(null); setBusy(false); setWord("");
    api.get(`/t/${code}/upload-reports/batch-history/${btype}/${batchId}/undo-preview`)
      .then((r) => {
        if (cancelled) return;
        setPreview(r.data);
        if (batch?.undo?.status === "processing") setUndo({ status: "processing" });
      })
      .catch((e) => { if (!cancelled) setPreviewError(errMsg(e)); });
    return () => { cancelled = true; };
  }, [open, code, btype, batchId]); // eslint-disable-line

  // poll while the rollback runs on the server
  useEffect(() => {
    if (!open || !batchId || undo?.status !== "processing") return;
    let stopped = false;
    let timer;
    const poll = async () => {
      try {
        const r = await api.get(`/t/${code}/upload-reports/batch-history/${btype}/${batchId}`, { timeout: 15000 });
        if (stopped) return;
        const u = r.data.undo || {};
        setUndo(u);
        if (u.status === "completed") {
          pokeLiveCollections();   // Phase 149
          const c = u.counts || {};
          const bits = [];
          if (c.moved) bits.push(`${c.moved} propert${c.moved === 1 ? "y" : "ies"} moved to the recycle bin`);
          if (c.reverted_properties) bits.push(`${c.reverted_properties} propert${c.reverted_properties === 1 ? "y" : "ies"} reverted`);
          if (c.demands_removed) bits.push(`${c.demands_removed} demand${c.demands_removed === 1 ? "" : "s"} removed`);
          toast.success(`Batch undone${bits.length ? " — " + bits.join(", ") : ""}`);
          doneRef.current?.(u);
          return;
        }
        if (u.status === "failed") return;
      } catch { /* transient — keep polling */ }
      if (!stopped) timer = setTimeout(poll, 1000);
    };
    poll();
    return () => { stopped = true; clearTimeout(timer); };
  }, [open, code, btype, batchId, undo?.status]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/upload-reports/batch-history/${btype}/${batchId}/undo`, { confirm: true });
      setUndo(r.data.undo || { status: "processing" });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const s = preview?.summary || {};
  const elig = preview?.eligibility || {};
  const running = undo?.status === "processing";
  const finished = undo?.status === "completed" || undo?.status === "failed";
  const pct = undo?.total ? Math.min(100, Math.round(((undo.processed || 0) / undo.total) * 100)) : 0;
  const confirmed = word.trim().toUpperCase() === CONFIRM_WORD;
  const kept = (s.properties_kept_with_payments || 0) + (s.demands_kept_with_payments || 0);
  const conflicts = (s.field_conflicts || 0) + (s.demand_conflicts || 0);
  const c = undo?.counts || {};

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!running) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-xl" data-testid="undo-batch-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Undo2 className="h-4 w-4" /> Undo this batch
          </DialogTitle>
          <DialogDescription className="break-all" data-testid="undo-batch-subtitle">
            <span className="font-medium text-foreground">{batch?.type_label}</span> · {batch?.filename || "batch"}
            {batch?.finished_at ? ` · processed ${fmtDateTime(batch.finished_at)}` : ""}
            {batch?.by ? ` · by ${batch.by}` : ""}
          </DialogDescription>
        </DialogHeader>

        {previewError && (
          <Alert variant="destructive" data-testid="undo-batch-preview-error">
            <AlertTitle>Could not load the preview</AlertTitle>
            <AlertDescription>{previewError}</AlertDescription>
          </Alert>
        )}
        {!preview && !previewError && (
          <div className="space-y-2" data-testid="undo-batch-preview-loading">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-9 w-1/2" />
          </div>
        )}

        {preview && !running && !finished && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2" data-testid="undo-batch-summary">
              <Stat value={s.properties_to_recycle_bin} label="Properties to recycle bin" testId="undo-batch-stat-trash" />
              <Stat value={s.properties_to_revert} label={`Properties reverted (${s.fields_to_revert || 0} field${s.fields_to_revert === 1 ? "" : "s"})`}
                testId="undo-batch-stat-revert" />
              <Stat value={(s.demands_to_remove || 0) + (s.demands_to_restore || 0)}
                label={s.demands_to_restore ? `Demands removed / restored` : "Demands removed"} testId="undo-batch-stat-demands" />
            </div>
            {(kept > 0 || conflicts > 0 || s.properties_already_removed > 0 || s.assignments_to_remove > 0 || s.custom_values_to_restore > 0) && (
              <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="undo-batch-notes">
                {kept > 0 && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 font-normal" data-testid="undo-batch-kept">
                    <ShieldCheck className="h-3 w-3 mr-1" /> {kept} kept — payments received
                  </Badge>
                )}
                {conflicts > 0 && (
                  <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-200 font-normal" data-testid="undo-batch-conflicts">
                    <AlertTriangle className="h-3 w-3 mr-1" /> {conflicts} changed again since — left as is
                  </Badge>
                )}
                {s.properties_already_removed > 0 && (
                  <Badge variant="outline" className="font-normal">{s.properties_already_removed} already removed earlier</Badge>
                )}
                {s.assignments_to_remove > 0 && (
                  <Badge variant="outline" className="font-normal">{s.assignments_to_remove} collector assignment{s.assignments_to_remove === 1 ? "" : "s"} removed</Badge>
                )}
                {s.custom_values_to_restore > 0 && (
                  <Badge variant="outline" className="font-normal">{s.custom_values_to_restore} custom value{s.custom_values_to_restore === 1 ? "" : "s"} restored</Badge>
                )}
              </div>
            )}
            {preview.sample_pins?.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5" data-testid="undo-batch-sample-pins">
                <span className="text-[11px] text-muted-foreground">e.g.</span>
                {preview.sample_pins.map((p) => <Badge key={p} variant="outline" className="font-mono text-[10px]">{p}</Badge>)}
              </div>
            )}
            {preview.method === "time_window" && (
              <Alert data-testid="undo-batch-method-warning">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Best-effort match</AlertTitle>
                <AlertDescription>This import predates record tagging; its properties were identified from the import's time window. Review the count before confirming.</AlertDescription>
              </Alert>
            )}

            {!elig.eligible ? (
              <Alert data-testid="undo-batch-ineligible">
                <Clock className="h-4 w-4" />
                <AlertTitle>Undo not available</AlertTitle>
                <AlertDescription>{preview.reason_text || "This batch cannot be undone."}</AlertDescription>
              </Alert>
            ) : (
              <>
                <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>
                    Created properties move to the recycle bin (restorable from Upload Status); updated fields go back to their
                    previous values; demands added by the batch are removed. Anything with payments is never touched.
                    {elig.seconds_left ? <> Undo stays available for <span className="font-medium">{fmtLeft(elig.seconds_left)}</span>.</> : null}
                  </span>
                </p>
                <div className="space-y-1.5 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                  <Label htmlFor="undo-batch-confirm-input" className="text-xs">
                    Type <span className="font-mono font-semibold">{CONFIRM_WORD}</span> to confirm
                  </Label>
                  <Input id="undo-batch-confirm-input" value={word} onChange={(e) => setWord(e.target.value)} autoComplete="off"
                    placeholder={CONFIRM_WORD} className="h-9 font-mono uppercase" data-testid="undo-batch-confirm-input" />
                </div>
              </>
            )}
          </div>
        )}

        {running && (
          <div className="space-y-2" role="status" aria-live="polite" data-testid="undo-batch-progress">
            <p className="text-sm font-medium">Undoing… {undo.total ? `${undo.processed || 0} / ${undo.total}` : ""}</p>
            <Progress value={pct} className="[&>div]:transition-transform [&>div]:duration-200" />
            <p className="text-xs text-muted-foreground">Keep this window open — large batches take a few moments.</p>
          </div>
        )}

        {undo?.status === "completed" && (
          <Alert data-testid="undo-batch-done">
            <ArchiveRestore className="h-4 w-4" />
            <AlertTitle>Batch undone</AlertTitle>
            <AlertDescription className="space-y-0.5">
              <p>
                {c.moved || 0} propert{c.moved === 1 ? "y" : "ies"} moved to the recycle bin
                {c.reverted_properties ? `, ${c.reverted_properties} reverted (${c.reverted_fields || 0} field${c.reverted_fields === 1 ? "" : "s"})` : ""}
                {c.demands_removed ? `, ${c.demands_removed} demand${c.demands_removed === 1 ? "" : "s"} removed` : ""}
                {c.demands_restored ? `, ${c.demands_restored} demand${c.demands_restored === 1 ? "" : "s"} restored` : ""}.
              </p>
              {(c.skipped_paid || c.conflicts || c.missing) ? (
                <p className="text-xs">
                  {c.skipped_paid ? `${c.skipped_paid} kept because payments were received. ` : ""}
                  {c.conflicts ? `${c.conflicts} item${c.conflicts === 1 ? "" : "s"} changed again since the batch and were left as is. ` : ""}
                  {c.missing ? `${c.missing} already removed earlier.` : ""}
                </p>
              ) : null}
            </AlertDescription>
          </Alert>
        )}
        {undo?.status === "failed" && (
          <Alert variant="destructive" data-testid="undo-batch-failed">
            <AlertTitle>Undo stopped with an error</AlertTitle>
            <AlertDescription>{undo.error || "Please try again."} Changes made so far are kept; running Undo again continues safely.</AlertDescription>
          </Alert>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={running} data-testid="undo-batch-close">
            {finished ? "Close" : "Cancel"}
          </Button>
          {!finished && (
            <Button variant="destructive" onClick={start} data-testid="undo-batch-confirm"
              disabled={busy || running || !preview || !elig.eligible || !confirmed}>
              <RotateCcw className="h-4 w-4 mr-2" />
              {running ? "Undoing…" : "Undo batch"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default UndoBatchDialog;
